import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import CadastroDataTable from '../components/CadastroDataTable/CadastroDataTable';
import api, { normalize } from '../services/api';
import './nfse/NfseConsulta.css';
import './PerfilPage.css';

function money(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Financeiro: boletos + contas a receber (contas bancárias ficam em Configurações). */
export default function FinanceiroPage() {
  const toast = useRef(null);
  const [contas, setContas] = useState([]);
  const [boletos, setBoletos] = useState([]);
  const [crs, setCrs] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boletoVisible, setBoletoVisible] = useState(false);
  const [formBoleto, setFormBoleto] = useState({
    contaBancariaId: null,
    agenteId: null,
    valor: null,
    vencimento: null,
  });
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [c, b, cr, a] = await Promise.all([
        api.get('/financeiro/contas-bancarias'),
        api.get('/financeiro/boletos'),
        api.get('/financeiro/contas-receber'),
        api.get('/agentes').catch(() => ({ data: [] })),
      ]);
      setContas(normalize(c.data));
      setBoletos(normalize(b.data));
      setCrs(normalize(cr.data));
      setAgentes(normalize(a.data));
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: e.response?.data?.message || 'Falha ao carregar financeiro.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const agenteOptions = useMemo(
    () =>
      agentes.map((a) => ({
        label: a.fantasia || a.razaosocial || a.cnpjcpf,
        value: a.id,
      })),
    [agentes]
  );

  const contaOptions = useMemo(
    () =>
      contas.map((c) => ({
        label: `${c.banco || c.provider} — ag ${c.agencia} / ${c.conta}`,
        value: c.id,
      })),
    [contas]
  );

  const gerarBoleto = async () => {
    if (!formBoleto.contaBancariaId || !formBoleto.agenteId || !formBoleto.valor || !formBoleto.vencimento) {
      toast.current?.show({ severity: 'warn', summary: 'Atenção', detail: 'Preencha conta, cliente, valor e vencimento.' });
      return;
    }
    setSalvando(true);
    try {
      const { data } = await api.post('/financeiro/boletos', {
        contaBancariaId: formBoleto.contaBancariaId,
        agenteId: formBoleto.agenteId,
        valor: formBoleto.valor,
        vencimento: formBoleto.vencimento,
      });
      const r = normalize(data);
      toast.current?.show({
        severity: 'success',
        summary: 'Boleto',
        detail: r.aviso || 'Boleto gerado (stub).',
      });
      setBoletoVisible(false);
      setFormBoleto({ contaBancariaId: null, agenteId: null, valor: null, vencimento: null });
      carregar();
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: e.response?.data?.message || 'Falha ao gerar boleto.',
      });
    } finally {
      setSalvando(false);
    }
  };

  const baixarCr = async (row) => {
    try {
      await api.post(`/financeiro/contas-receber/${row.id}/baixa`);
      toast.current?.show({ severity: 'success', summary: 'Baixa', detail: 'Conta marcada como paga.' });
      carregar();
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: e.response?.data?.message || 'Falha na baixa.',
      });
    }
  };

  const boletoCols = [
    { field: 'id', header: 'ID', style: { width: 70 } },
    { field: 'nossoNumero', header: 'Nosso número' },
    {
      field: 'valor',
      header: 'Valor',
      body: (r) => money(r.valor),
      style: { width: 120 },
    },
    {
      field: 'vencimento',
      header: 'Vencimento',
      body: (r) => (r.vencimento ? String(r.vencimento).slice(0, 10) : '—'),
    },
    { field: 'status', header: 'Status', style: { width: 100 } },
  ];

  const crCols = [
    { field: 'id', header: 'ID', style: { width: 70 } },
    { field: 'descricao', header: 'Descrição' },
    {
      field: 'valorOriginal',
      header: 'Valor',
      body: (r) => money(r.valorOriginal),
      style: { width: 120 },
    },
    {
      field: 'valorAberto',
      header: 'Aberto',
      body: (r) => money(r.valorAberto),
      style: { width: 120 },
    },
    { field: 'status', header: 'Status', style: { width: 100 } },
    {
      field: '_acoes',
      header: 'Ações',
      sortable: false,
      alwaysVisible: true,
      style: { width: 120 },
      body: (r) => (
        <Button
          label="Baixa"
          icon="pi pi-check"
          className="p-button-sm p-button-outlined"
          disabled={r.status === 'Pago'}
          onClick={() => baixarCr(r)}
        />
      ),
    },
  ];

  return (
    <div className="nfse-consulta-page">
      <Toast ref={toast} />
      <div className="nfse-consulta-header">
        <h1>Financeiro</h1>
      </div>

      <TabView className="financeiro-tabs">
        <TabPanel header="Boletos">
          <div style={{ marginBottom: 12 }}>
            <Button label="Gerar boleto" icon="pi pi-plus" onClick={() => setBoletoVisible(true)} />
          </div>
          <CadastroDataTable
            value={boletos}
            columns={boletoCols}
            storageKey="fin-boletos"
            loading={loading}
            title="Boletos"
            emptyMessage="Nenhum boleto."
            globalFilterFields={['nossoNumero', 'status']}
          />
        </TabPanel>
        <TabPanel header="Contas a receber">
          <CadastroDataTable
            value={crs}
            columns={crCols}
            storageKey="fin-cr"
            loading={loading}
            title="Contas a receber"
            emptyMessage="Nenhuma conta a receber."
            globalFilterFields={['descricao', 'status']}
          />
        </TabPanel>
      </TabView>

      <Dialog
        header="Gerar boleto"
        visible={boletoVisible}
        onHide={() => setBoletoVisible(false)}
        style={{ width: 480 }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Cancelar" className="p-button-text" onClick={() => setBoletoVisible(false)} />
            <Button label="Gerar" icon="pi pi-check" loading={salvando} onClick={gerarBoleto} />
          </div>
        }
      >
        <div className="nfse-emissao-grid">
          <div className="nfse-emissao-field nfse-emissao-field-full">
            <label>Conta bancária</label>
            <Dropdown
              value={formBoleto.contaBancariaId}
              options={contaOptions}
              onChange={(e) => setFormBoleto((p) => ({ ...p, contaBancariaId: e.value }))}
              placeholder="Selecione"
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field nfse-emissao-field-full">
            <label>Cliente</label>
            <Dropdown
              value={formBoleto.agenteId}
              options={agenteOptions}
              onChange={(e) => setFormBoleto((p) => ({ ...p, agenteId: e.value }))}
              placeholder="Selecione"
              filter
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field">
            <label>Valor</label>
            <InputNumber
              value={formBoleto.valor}
              onValueChange={(e) => setFormBoleto((p) => ({ ...p, valor: e.value }))}
              mode="currency"
              currency="BRL"
              locale="pt-BR"
              className="w-full"
            />
          </div>
          <div className="nfse-emissao-field">
            <label>Vencimento</label>
            <Calendar
              value={formBoleto.vencimento}
              onChange={(e) => setFormBoleto((p) => ({ ...p, vencimento: e.value }))}
              dateFormat="dd/mm/yy"
              showIcon
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
